import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BiChevronDown, BiDotsVerticalRounded, BiMenu } from 'react-icons/bi';
import { Menu, MenuItem } from '@material-ui/core';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import { Complement } from '../../hooks/complements';

import ImagePlaceholder from '../../assets/no_image.png';

import {
  Container,
  Header,
  ActionButton,
  Main,
  Table,
  TableHeader,
  TableCell,
  TableBody,
  TableRow,
  ComplementItemDetails,
  Imagem,
  MandatoryIndicator,
} from './styles';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

type ComplementAccordionProps = {
  complement: Complement;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onEdit(): void;
  onDelete(): void;
};

const INITIAL_ACCORDION_HEIGHT = 66.3;

const ComplementAccordion: React.FC<ComplementAccordionProps> = ({
  complement,
  dragHandleProps,
  onEdit,
  onDelete,
}) => {
  const mainRef = useRef<HTMLDivElement>(null);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [accordionHeight, setAccordionHeight] = useState(
    INITIAL_ACCORDION_HEIGHT,
  );
  const [isOpenConfirmDeleteModal, setIsOpenConfirmDeleteModal] = useState(
    false,
  );

  const handleAccordionHeight = useCallback(() => {
    if (isExpanded && mainRef.current?.offsetHeight) {
      const height = mainRef.current.offsetHeight;

      setAccordionHeight(height + INITIAL_ACCORDION_HEIGHT);
    } else {
      setAccordionHeight(INITIAL_ACCORDION_HEIGHT);
    }
  }, [isExpanded]);

  const handleToggleAccordion = useCallback(() => {
    setIsExpanded((oldState) => !oldState);
  }, []);

  const handleOpenOptions = useCallback(() => {
    setIsOptionsOpen(true);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setIsOptionsOpen(false);
  }, []);

  const handleComplementItemImage = useCallback(
    (image?: File | string): string => {
      if (!image) {
        return ImagePlaceholder;
      }

      if (image instanceof File) {
        return URL.createObjectURL(image);
      }

      return `${process.env.REACT_APP_SERVER_URL}/files/${image}`;
    },
    [],
  );

  const handleEditComplement = useCallback(() => {
    handleCloseOptions();
    onEdit();
  }, [onEdit, handleCloseOptions]);

  const handleDeleteComplement = useCallback(() => {
    handleCloseOptions();
    onDelete();
  }, [onDelete, handleCloseOptions]);

  useEffect(() => {
    handleAccordionHeight();
  }, [complement, isExpanded, handleAccordionHeight]);

  useEffect(() => {
    window.addEventListener('resize', handleAccordionHeight);
  });

  const handleOpenConfirmDeleteModal = useCallback(() => {
    setIsOpenConfirmDeleteModal(true);
    setIsOptionsOpen(false);
  }, []);

  const handleCloseConfirmDeleteModal = useCallback(() => {
    setIsOpenConfirmDeleteModal(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    handleDeleteComplement();
    handleCloseConfirmDeleteModal();
  }, []);

  return (
    <Container style={{ height: accordionHeight }}>
      <Header>
        <div {...dragHandleProps}>
          <BiMenu size={22} style={{ marginTop: -2 }} />
          <h1>{complement.name}</h1>
        </div>
        <div>
          <MandatoryIndicator isRequired={!!complement.isRequired}>
            <span>{complement.isRequired ? 'Obrigatório' : 'Opcional'}</span>
          </MandatoryIndicator>
          <ActionButton
            ref={optionsButtonRef}
            type="button"
            onClick={handleOpenOptions}
          >
            <BiDotsVerticalRounded />
          </ActionButton>
          <Menu
            id="asdasdjkasd"
            anchorEl={optionsButtonRef.current}
            keepMounted
            open={isOptionsOpen}
            onClose={handleCloseOptions}
            style={{ zIndex: 9999999999 }}
            PaperProps={{
              style: {
                maxHeight: 48 * 4.5,
                width: '20ch',
              },
            }}
          >
            <MenuItem onClick={handleEditComplement}>Editar</MenuItem>
            <MenuItem onClick={handleOpenConfirmDeleteModal}>Excluir</MenuItem>
          </Menu>
          <ActionButton
            isExpanded={isExpanded}
            className="arrow"
            type="button"
            onClick={handleToggleAccordion}
          >
            <BiChevronDown />
          </ActionButton>
        </div>
      </Header>
      <Main
        ref={mainRef}
        style={{
          overflowY: 'hidden',
          overflowX: 'auto',
        }}
      >
        <Table style={{ minWidth: 'max-content' }}>
          <hr />
          <TableHeader>
            <TableCell style={{ minWidth: '250px' }}>Item</TableCell>
            <TableCell>Preço</TableCell>
            <TableCell />
          </TableHeader>
          <TableBody>
            {complement.items &&
              complement.items
                .sort((a, b) => (a?.order ?? 1) - (b?.order ?? 1))
                .map((complementItem) => (
                  <TableRow key={complementItem.id}>
                    <TableCell style={{ minWidth: '250px' }}>
                      <ComplementItemDetails>
                        <Imagem
                          defaultImg={ImagePlaceholder}
                          image={handleComplementItemImage(
                            complementItem.image,
                          )}
                        >
                          <div />
                        </Imagem>
                        {complementItem.name}
                      </ComplementItemDetails>
                    </TableCell>
                    <TableCell>
                      {Number(complementItem.price).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Main>
      {isOpenConfirmDeleteModal && (
        <ConfirmDeleteModal
          isOpen={isOpenConfirmDeleteModal}
          handleClose={handleCloseConfirmDeleteModal}
          handleDelete={handleConfirmCancel}
        />
      )}
    </Container>
  );
};

export default ComplementAccordion;
